import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Rating,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../services/api';
import ToastService from '../../core/notifications/ToastService';
import QRCodeGenerator from '../../components/QRCode/QRCodeGenerator';

interface Booking {
  id: number;
  booking_number?: string;
  bookingNumber?: string;
  user_name?: string;
  userName?: string;
  user_phone?: string;
  userPhone?: string;
  user_email?: string;
  userEmail?: string;
  gym_name?: string;
  gymName?: string;
  gym_id?: number;
  booking_date?: string;
  date?: string;
  start_time?: string;
  startTime?: string;
  end_time?: string;
  endTime?: string;
  status: 'pending' | 'confirmed' | 'checked-in' | 'completed' | 'cancelled';
  check_in_time?: string;
  checkInTime?: string;
  check_out_time?: string;
  checkOutTime?: string;
  total_amount?: number;
  amount?: number;
  payment_method?: string;
  payment_status?: string;
  notes?: string;
  rating?: number;
  review?: string;
  created_at?: string;
}

export const BookingDetailsFullPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [showCheckOutDialog, setShowCheckOutDialog] = useState(false);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');

  const fetchBookingDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<Booking>(`/bookings/${bookingId}`);
      setBooking(data);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to fetch booking details';
      setError(message);
      ToastService.error(message);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId, fetchBookingDetails]);

  const normalizeBooking = (b: Booking): Booking => ({
    ...b,
    bookingNumber: b.booking_number || b.bookingNumber || '',
    userName: b.user_name || b.userName || '',
    userPhone: b.user_phone || b.userPhone || '',
    userEmail: b.user_email || b.userEmail || '',
    gymName: b.gym_name || b.gymName || '',
    date: b.booking_date || b.date || '',
    startTime: b.start_time || b.startTime || '',
    endTime: b.end_time || b.endTime || '',
    checkInTime: b.check_in_time || b.checkInTime,
    checkOutTime: b.check_out_time || b.checkOutTime,
    amount: b.total_amount || b.amount || 0,
  });

  const normalizedBooking = booking ? normalizeBooking(booking) : null;

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      confirmed: '#3B82F6',
      'checked-in': '#F59E0B',
      completed: '#10B981',
      cancelled: '#EF4444',
      pending: '#14B8A6'
    };
    return colors[status] || '#6B7280';
  };

  const handleCheckIn = async () => {
    if (!normalizedBooking) return;
    try {
      setActionLoading(true);
      await apiClient.post(`/bookings/${normalizedBooking.id}/check-in`, {
        check_in_method: 'manual'
      });
      setBooking(prev => prev ? { ...prev, status: 'checked-in' as const } : null);
      ToastService.success('Check-in successful');
      setShowCheckInDialog(false);
    } catch (err: any) {
      ToastService.error(err.response?.data?.message || 'Check-in failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!normalizedBooking) return;
    try {
      setActionLoading(true);
      await apiClient.post(`/bookings/${normalizedBooking.id}/check-out`, {});
      setBooking(prev => prev ? { ...prev, status: 'completed' as const } : null);
      ToastService.success('Check-out successful');
      setShowCheckOutDialog(false);
    } catch (err: any) {
      ToastService.error(err.response?.data?.message || 'Check-out failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitRating = async () => {
    if (!normalizedBooking) return;
    try {
      setActionLoading(true);
      await apiClient.post(`/bookings/${normalizedBooking.id}/rating`, {
        rating,
        review
      });
      setBooking(prev => prev ? { ...prev, rating, review } : null);
      ToastService.success('Rating submitted');
      setShowRatingDialog(false);
    } catch (err: any) {
      ToastService.error(err.response?.data?.message || 'Failed to submit rating');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !normalizedBooking) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Booking not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/bookings')}>
          Back to Bookings
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/bookings')}
            sx={{ mb: 2 }}
          >
            Back to Bookings
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Booking Details
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => setShowQRCode(true)}
          >
            QR Code
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Booking Info Card */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {normalizedBooking.bookingNumber}
                </Typography>
                <Chip
                  label={normalizedBooking.status}
                  sx={{
                    backgroundColor: getStatusColor(normalizedBooking.status) + '20',
                    color: getStatusColor(normalizedBooking.status),
                    fontWeight: 'bold'
                  }}
                />
              </Box>
              <Divider sx={{ my: 2 }} />

              {/* Customer Information */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                Customer Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {normalizedBooking.userName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Phone</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {normalizedBooking.userPhone}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Email</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {normalizedBooking.userEmail || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Activity Details */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                Activity Details
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Gym</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {normalizedBooking.gymName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {normalizedBooking.date}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Time Slot</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {normalizedBooking.startTime} - {normalizedBooking.endTime}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Check-in/Check-out Times */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                Check-in/Check-out
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Check-in Time</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {normalizedBooking.checkInTime || 'Not checked in'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Check-out Time</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {normalizedBooking.checkOutTime || 'Not checked out'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Payment Information */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                Payment Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Amount</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#14B8A6' }}>
                    ₹{normalizedBooking.amount}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Payment Method</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {normalizedBooking.payment_method || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Notes */}
          {normalizedBooking.notes && (
            <Card>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Notes
                </Typography>
                <Typography variant="body2">
                  {normalizedBooking.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                Actions
              </Typography>

              {normalizedBooking.status === 'confirmed' && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setShowCheckInDialog(true)}
                  sx={{ mb: 2 }}
                >
                  Check In
                </Button>
              )}

              {normalizedBooking.status === 'checked-in' && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CloseIcon />}
                  onClick={() => setShowCheckOutDialog(true)}
                  sx={{ mb: 2 }}
                >
                  Check Out
                </Button>
              )}

              {normalizedBooking.status === 'completed' && !normalizedBooking.rating && (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setShowRatingDialog(true)}
                  sx={{ mb: 2 }}
                >
                  Add Rating
                </Button>
              )}

              {normalizedBooking.rating && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                    Your Rating
                  </Typography>
                  <Rating value={normalizedBooking.rating} readOnly />
                  {normalizedBooking.review && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {normalizedBooking.review}
                    </Typography>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" sx={{ color: '#666' }}>
                <strong>Booking ID:</strong> {normalizedBooking.id}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                <strong>Created:</strong> {normalizedBooking.created_at || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Check-In Dialog */}
      <Dialog open={showCheckInDialog} onClose={() => setShowCheckInDialog(false)}>
        <DialogTitle>Confirm Check-In</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Check in for <strong>{normalizedBooking.userName}</strong> at <strong>{normalizedBooking.gymName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCheckInDialog(false)}>Cancel</Button>
          <Button onClick={handleCheckIn} variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Check In'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check-Out Dialog */}
      <Dialog open={showCheckOutDialog} onClose={() => setShowCheckOutDialog(false)}>
        <DialogTitle>Confirm Check-Out</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Check out for <strong>{normalizedBooking.userName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCheckOutDialog(false)}>Cancel</Button>
          <Button onClick={handleCheckOut} variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Check Out'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={showRatingDialog} onClose={() => setShowRatingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rate This Booking</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Rating</Typography>
            <Rating
              value={rating}
              onChange={(_, val) => setRating(val || 0)}
              size="large"
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Add your review (optional)"
            value={review}
            onChange={(e) => setReview(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRatingDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitRating} variant="contained" disabled={actionLoading || rating === 0}>
            {actionLoading ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <QRCodeGenerator
        bookingNumber={normalizedBooking.bookingNumber || ''}
        userName={normalizedBooking.userName || ''}
        gymName={normalizedBooking.gymName || ''}
        open={showQRCode}
        onClose={() => setShowQRCode(false)}
      />
    </Container>
  );
};

export default BookingDetailsFullPage;
